import { useTranslations } from '@vocab/react';
import { Badge, Box, Stack, Text } from 'braid-design-system';
import type { FC, PropsWithChildren, ReactNode } from 'react';

import { PostType } from 'src/graphql/__generated__';
import type { Location } from 'src/types';

import { useConfig } from '../../../hooks/useConfig';
import { DateFormat } from '../DateFormat/DateFormat';
import {
  createCandidateManagementLink,
  createEditDraftJobLink,
} from '../JobActions/links';
import { Skeleton } from '../Skeleton/Skeleton';
import { TrackingLink } from '../TrackingLink/TrackingLink';
import { TruncateText } from '../TruncateText';

import translations from './.vocab';

import { breakWord } from '../../styles/BreakWord.css';

export interface JobTitleAndLocationProps {
  isDraft: boolean;
  id: string;
  postType: PostType;
  advertiserReference?: string;
  agencyReference?: string;
  createdBy?: string;
  createdDate?: string;
  title?: string;
  location?: Location;
  loading?: boolean;
}

export const JobTitleAndLocation: FC<
  PropsWithChildren<JobTitleAndLocationProps>
> = ({
  isDraft,
  id,
  postType,
  advertiserReference,
  agencyReference,
  createdBy,
  createdDate,
  title,
  location,
  loading = false,
}) => {
  const { t } = useTranslations(translations);
  const jobTitle = Boolean(title) ? title : t('Untitled');
  const jobTitleLinkUrl = isDraft
    ? createEditDraftJobLink(id)
    : createCandidateManagementLink(id);

  let createInfo: ReactNode = '';
  if (createdDate && createdBy) {
    createInfo = t('Created on date by person', {
      author: createdBy,
      FormattedDate: () => (
        <DateFormat key={`${id}-create-date`} date={createdDate!} />
      ),
    });
  } else if (createdDate) {
    createInfo = t('Created on date', {
      FormattedDate: () => (
        <DateFormat key={`${id}-create-date`} date={createdDate!} />
      ),
    });
  } else if (createdBy) {
    createInfo = t('Created by person', { author: createdBy });
  }

  const { urlResolver } = useConfig();

  return (
    <Stack space="small">
      <TrackingLink
        href={urlResolver({
          path: jobTitleLinkUrl,
        })}
        eventName="job_title_pressed"
        eventAttributes={{ jobId: id }}
        weight="weak"
      >
        <Box className={breakWord}>
          <Text
            weight="regular"
            data={{ intercomid: 'jobTitle', testid: 'jobTitle' }}
            size="small"
          >
            <Skeleton loading={loading} numberOfCharacters={15}>
              <TruncateText>{jobTitle}</TruncateText>
            </Skeleton>
          </Text>
        </Box>
      </TrackingLink>

      {(Boolean(advertiserReference) || Boolean(agencyReference)) && (
        <Text data={{ testid: 'references' }} tone="secondary" size="small">
          <Skeleton loading={loading} numberOfCharacters={15}>
            {advertiserReference ?? ''}
            {advertiserReference && agencyReference ? ' | ' : ''}
            {agencyReference ?? ''}
          </Skeleton>
        </Text>
      )}

      {Boolean(location?.description) && (
        <Text data={{ testid: 'location' }} tone="secondary" size="small">
          <Skeleton loading={loading} numberOfCharacters={15}>
            <TruncateText>{location?.description}</TruncateText>
          </Skeleton>
        </Text>
      )}

      {createInfo !== '' && (
        <Text tone="secondary" data={{ 'hj-masked': 'true' }} size="small">
          <Skeleton loading={loading} numberOfCharacters={30}>
            <TruncateText>{createInfo}</TruncateText>
          </Skeleton>
        </Text>
      )}

      {postType === PostType.INDIRECT && (
        <Skeleton loading={loading} numberOfCharacters={30}>
          <Badge tone="neutral">{t('Uploaded')}</Badge>
        </Skeleton>
      )}

      {postType === PostType.PAID_AGGREGATION && (
        <Skeleton loading={loading} numberOfCharacters={30}>
          <Badge tone="neutral">{t('Ad Sync')}</Badge>
        </Skeleton>
      )}
    </Stack>
  );
};
